import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTourAutoStart } from '@/hooks/useTourAutoStart';
import { staffDashboardTourId, staffDashboardSteps } from '@/tours/staff';
import {
  Package,
  CheckCircle2,
  ArrowRight,
  Upload,
  ClipboardList,
} from 'lucide-react';
import { Card, LoadingSpinner, ErrorMessage } from '@/components/ui';
import { OrderStatusBadge } from '@/components/features/orders';
import { ordersApi } from '@/services/api/ordersApi';
import { queryKeys } from '@/services/api/queryClient';
import { OrderStatus } from '@/types/enums';
import { cn } from '@/lib/utils';

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
  } catch {
    return dateStr;
  }
}

const DashboardPage = () => {
  useTourAutoStart({ tourId: staffDashboardTourId, steps: staffDashboardSteps });
  const navigate = useNavigate();

  const { data: allOrders, isLoading: loadingOrders, error: ordersError } = useQuery({
    queryKey: queryKeys.orderList('dashboard'),
    queryFn: () => ordersApi.getList({ page: 1, limit: 100 }),
    staleTime: 60 * 1000,
  });

  if (loadingOrders) {
    return <LoadingSpinner size="lg" className="py-24" />;
  }

  if (ordersError) {
    return <ErrorMessage message="Не удалось загрузить данные" />;
  }

  const orders = allOrders?.data ?? [];
  const arrivedCount = orders.filter((o) => o.status === OrderStatus.ARRIVED).length;
  const completedToday = orders.filter((o) => {
    if (o.status !== OrderStatus.COMPLETED) return false;
    if (!o.handed_off_at) return false;
    const today = new Date().toISOString().slice(0, 10);
    return o.handed_off_at.startsWith(today);
  }).length;

  const recentOrders = [...orders]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 8);

  const stats = [
    { label: 'Ожидают выдачи', value: arrivedCount, icon: Package, color: 'text-blue-600 bg-blue-100' },
    { label: 'Выдано сегодня', value: completedToday, icon: CheckCircle2, color: 'text-green-600 bg-green-100' },
  ];

  const quickActions = [
    { label: 'Загрузить поставку', icon: Upload, path: '/receiving' },
    { label: 'Все заказы', icon: Package, path: '/orders' },
    { label: 'Инвентаризация', icon: ClipboardList, path: '/inventory' },
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Панель управления</h1>
        <p className="text-sm text-gray-500 mt-1">Обзор активности пункта выдачи</p>
      </div>

      <div data-tour="staff:stats-cards" className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {stats.map((stat) => (
          <Card key={stat.label} padding="md">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{stat.value}</p>
              </div>
              <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', stat.color)}>
                <stat.icon className="w-5 h-5" />
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div data-tour="staff:recent-orders" className="lg:col-span-2">
          <Card>
            <Card.Header>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Последние заказы</h2>
              <button
                onClick={() => navigate('/orders')}
                className="text-sm font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1 transition-colors"
              >
                Все заказы
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {recentOrders.length === 0 ? (
              <div className="py-12 text-center text-gray-400">
                <Package className="w-10 h-10 mx-auto mb-2" />
                <p className="text-sm">Заказов пока нет</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {recentOrders.map((order) => (
                   <button
                    key={order.id}
                    onClick={() => navigate(`/orders/${order.id}`)}
                    className="w-full flex items-center gap-3 md:gap-4 px-4 md:px-6 py-3.5 hover:bg-gray-50/50 transition-colors text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{order.order_number}</p>
                      <p className="text-xs text-gray-500">{formatDate(order.created_at)}</p>
                    </div>
                    <OrderStatusBadge status={order.status} />
                    <ArrowRight className="w-4 h-4 text-gray-300 shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </Card.Body>
          </Card>
        </div>

        <div data-tour="staff:quick-actions">
          <Card>
            <Card.Header>
              <h2 className="text-lg font-semibold text-gray-900">Быстрые действия</h2>
            </Card.Header>
            <Card.Body>
              <div className="space-y-2">
                {quickActions.map((action) => (
                  <button
                    key={action.path}
                    onClick={() => navigate(action.path)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-gray-200 hover:bg-gray-50/50 transition-all duration-200 text-left group"
                  >
                    <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center group-hover:bg-primary-100 transition-colors">
                      <action.icon className="w-4 h-4 text-primary-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">{action.label}</span>
                    <ArrowRight className="w-4 h-4 text-gray-300 ml-auto group-hover:text-gray-500 transition-colors" />
                  </button>
                ))}
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
